import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, PaperPlaneTilt, Trash, UserCircle, ShieldCheck, X, CheckCircle, PencilSimple } from "@phosphor-icons/react";

// Must stay in sync with backend GUEST_NAV_KEYS (server.py) and NAV in Layout.js.
const NAV_ITEMS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "leads", label: "Leads" },
  { key: "marketing", label: "Marketing" },
  { key: "analytics", label: "Analytics" },
  { key: "reports", label: "Reports" },
];
const DEFAULT_NAV = ["dashboard", "leads", "marketing"];

/**
 * Admin panel for guest users. Guests sign in with email/password + email 2FA.
 * Admins set: read-only scope (departments + internal users) AND sidebar
 * visibility (which pages the guest can see).
 */
export default function GuestsTab() {
  const [guests, setGuests] = useState([]);
  const [staff, setStaff] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogState, setDialogState] = useState({ open: false, guest: null });

  const load = async () => {
    setLoading(true);
    try {
      const [g, u, d] = await Promise.all([
        api.get("/users/guests"),
        api.get("/users"),
        api.get("/users/departments"),
      ]);
      setGuests(g.data || []);
      setStaff((u.data || []).filter((s) => s.role !== "guest"));
      setDepartments(d.data?.departments || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resendInvite = async (id) => {
    try {
      const { data } = await api.post(`/users/guests/${id}/resend-invite`);
      const st = data.email_status;
      toast.success(
        st === "sent" ? "Invitation email sent."
        : st === "suppressed" ? "Invitation queued (outbound emails are suppressed in preview)."
        : `Invitation status: ${st}`
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend invitation.");
    }
  };

  const removeGuest = async (id, email) => {
    if (!window.confirm(`Remove guest ${email}? Their access will be revoked immediately.`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Guest removed.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove guest.");
    }
  };

  const statusChip = (g) => {
    if (g.status === "disabled") return <span className="px-2 py-0.5 rounded-sm text-[10px] font-medium bg-slate-200 text-slate-700">Disabled</span>;
    if (!g.password_hash) return <span className="px-2 py-0.5 rounded-sm text-[10px] font-medium bg-amber-100 text-amber-800">Invite sent</span>;
    return <span className="px-2 py-0.5 rounded-sm text-[10px] font-medium bg-emerald-100 text-emerald-800">Active</span>;
  };

  const accessChip = (g) => (
    g.guest_can_edit
      ? <span className="px-2 py-0.5 rounded-sm text-[10px] font-medium bg-amber-100 text-amber-800" title="Can create & edit records they can see">Can edit</span>
      : <span className="px-2 py-0.5 rounded-sm text-[10px] font-medium bg-slate-100 text-slate-700" title="View-only">Read only</span>
  );

  return (
    <div data-testid="guests-tab" className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="label-tracking mb-1">External access</div>
          <h2 className="font-display text-2xl text-slate-900">Guest users</h2>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Give external stakeholders (clients, referral partners, auditors) a limited, read-only view. Guests sign in with email + password + a 6-digit email code. You can change their access rights any time.
          </p>
        </div>
        <Button
          data-testid="add-guest-btn"
          onClick={() => setDialogState({ open: true, guest: null })}
          className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2 shrink-0"
        >
          <Plus size={16} /> Invite guest
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : guests.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-sm p-10 text-center">
          <UserCircle size={36} className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">No guest users yet. Click "Invite guest" to add one.</p>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500">
                <th className="py-3 px-4 label-tracking !text-[0.65rem]">Guest</th>
                <th className="py-3 px-4 label-tracking !text-[0.65rem]">Status</th>
                <th className="py-3 px-4 label-tracking !text-[0.65rem]">Scope</th>
                <th className="py-3 px-4 label-tracking !text-[0.65rem]">Sees pages</th>
                <th className="py-3 px-4 label-tracking !text-[0.65rem]">Last sign-in</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {guests.map((g) => {
                const nav = (g.guest_allowed_nav && g.guest_allowed_nav.length > 0) ? g.guest_allowed_nav : DEFAULT_NAV;
                const navLabels = NAV_ITEMS.filter((n) => nav.includes(n.key)).map((n) => n.label);
                return (
                  <tr key={g.id} className="border-b border-slate-100" data-testid={`guest-row-${g.email}`}>
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-900">{g.name || g.email.split("@")[0]}</div>
                      <div className="text-xs text-slate-500 font-mono">{g.email}</div>
                    </td>
                    <td className="py-3 px-4"><div className="flex flex-wrap gap-1">{statusChip(g)}{accessChip(g)}</div></td>
                    <td className="py-3 px-4 text-xs text-slate-600">
                      {(g.guest_scope_departments || []).length === 0 ? (
                        <span className="text-slate-400 italic">No departments — sees nothing</span>
                      ) : (
                        <div className="flex flex-wrap gap-1 max-w-[260px]">
                          {(g.guest_scope_departments || []).map((d) => (
                            <span key={`d-${d}`} className="px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-700 text-[10px]">{d}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-600">
                      <div className="flex flex-wrap gap-1 max-w-[220px]">
                        {navLabels.map((l) => (
                          <span key={l} className="px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-800 text-[10px]">{l}</span>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-500">
                      {g.last_login_at ? new Date(g.last_login_at).toLocaleString() : "—"}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end gap-1">
                        <Button
                          data-testid={`guest-edit-${g.email}`}
                          size="sm" variant="outline"
                          onClick={() => setDialogState({ open: true, guest: g })}
                          className="h-8 rounded-sm gap-1 border-slate-300"
                          title="Edit access rights"
                        >
                          <PencilSimple size={14} /> Edit
                        </Button>
                        <Button
                          data-testid={`guest-resend-${g.email}`}
                          size="sm" variant="outline"
                          onClick={() => resendInvite(g.id)}
                          className="h-8 rounded-sm gap-1 border-slate-300"
                          title="Send a new invitation email"
                        >
                          <PaperPlaneTilt size={14} />
                        </Button>
                        <Button
                          data-testid={`guest-delete-${g.email}`}
                          size="sm" variant="outline"
                          onClick={() => removeGuest(g.id, g.email)}
                          className="h-8 rounded-sm gap-1 border-slate-300 text-rose-600 hover:text-rose-700 hover:border-rose-300"
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <GuestFormDialog
        state={dialogState}
        onOpenChange={(open) => setDialogState((s) => ({ ...s, open }))}
        staff={staff}
        departments={departments}
        onSaved={load}
      />

      <div className="text-xs text-slate-500 pt-2 flex items-start gap-2">
        <ShieldCheck size={16} className="text-emerald-600 shrink-0 mt-0.5" />
        <div>
          Guests default to read-only — flip <strong>Access level</strong> to <em>Can edit</em> on individual guests who need to make changes. Every sign-in still requires a fresh 6-digit code emailed to them (or a trusted device from the last 30 days).
        </div>
      </div>
    </div>
  );
}

function GuestFormDialog({ state, onOpenChange, staff, departments, onSaved }) {
  const isEdit = !!state.guest;
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [scopeDepts, setScopeDepts] = useState([]);
  const [scopeAssignees, setScopeAssignees] = useState([]);
  const [allowedNav, setAllowedNav] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!state.open) return;
    const g = state.guest;
    setEmail(g?.email || "");
    setName(g?.name || "");
    setScopeDepts(g?.guest_scope_departments || []);
    setScopeAssignees(g?.guest_scope_assignees || []);
    setAllowedNav(
      (g?.guest_allowed_nav && g.guest_allowed_nav.length > 0)
        ? g.guest_allowed_nav
        : DEFAULT_NAV,
    );
    setCanEdit(!!g?.guest_can_edit);
  }, [state.open, state.guest]);

  const toggle = (arr, setArr, value) => {
    setArr(arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value]);
  };

  const activeStaff = useMemo(
    () => staff.filter((u) => u.status === "active").sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email)),
    [staff],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Enter a valid email.");
    setSaving(true);
    try {
      // Guests are scoped by department only — the per-user assignee list has
      // been dropped, so we always send an empty array to clear legacy data.
      const scopePayload = {
        name: name.trim() || null,
        guest_scope_departments: scopeDepts,
        guest_scope_assignees: [],
        guest_allowed_nav: allowedNav,
        guest_can_edit: canEdit,
      };
      if (isEdit) {
        await api.patch(`/users/${state.guest.id}`, scopePayload);
        toast.success("Guest access updated.");
      } else {
        await api.post("/users/guests", {
          email: email.trim().toLowerCase(),
          ...scopePayload,
        });
        toast.success("Guest invited — invitation email queued.");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save guest.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="guest-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit guest access" : "Invite a guest user"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
            <Input
              data-testid="guest-form-email"
              type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isEdit}
              className={isEdit ? "bg-slate-50" : ""}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Name (optional)</label>
            <Input data-testid="guest-form-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          {/* Edit permission toggle */}
          <div className="border border-slate-200 rounded-sm p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs font-medium text-slate-900">Access level</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {canEdit
                    ? "Can edit — this guest can modify records they have access to."
                    : "Read only — this guest can view but cannot change anything."}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  data-testid="access-readonly"
                  onClick={() => setCanEdit(false)}
                  className={`px-2 py-1 rounded-sm text-xs border transition-colors ${!canEdit ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
                >
                  Read only
                </button>
                <button
                  type="button"
                  data-testid="access-can-edit"
                  onClick={() => setCanEdit(true)}
                  className={`px-2 py-1 rounded-sm text-xs border transition-colors ${canEdit ? "bg-amber-600 text-white border-amber-600" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
                >
                  Can edit
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar visibility */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">Sidebar pages this guest can see</label>
            <div className="flex flex-wrap gap-1.5">
              {NAV_ITEMS.map((n) => (
                <button
                  key={n.key} type="button"
                  onClick={() => toggle(allowedNav, setAllowedNav, n.key)}
                  data-testid={`nav-toggle-${n.key}`}
                  className={`px-2 py-1 rounded-sm text-xs border transition-colors ${
                    allowedNav.includes(n.key)
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                  }`}
                >
                  {allowedNav.includes(n.key) && <CheckCircle size={12} weight="fill" className="inline mr-1" />}
                  {n.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">Tip: leave at least Dashboard on so they have a landing page.</p>
          </div>

          {/* Scope departments */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">
              Departments this guest can see
              <span className="text-slate-400 font-normal ml-1">(they'll see every lead & marketing item in these depts)</span>
            </label>
            {departments.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No departments synced yet.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {departments.map((d) => (
                  <button
                    key={d} type="button"
                    onClick={() => toggle(scopeDepts, setScopeDepts, d)}
                    data-testid={`scope-dept-${d}`}
                    className={`px-2 py-1 rounded-sm text-xs border transition-colors ${scopeDepts.includes(d) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
                  >
                    {scopeDepts.includes(d) && <CheckCircle size={12} weight="fill" className="inline mr-1" />}
                    {d}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Scope assignees intentionally removed — guests see everyone in
              the departments granted above; per-user allow-listing was too
              restrictive for the way Barnard Inc's referral partners work. */}

          {!isEdit && (
            <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-2">
              An invitation email with a 24-hour set-password link will be sent immediately.
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button
              type="submit" disabled={saving || !email}
              data-testid="guest-form-submit"
              className="rounded-sm bg-slate-900 hover:bg-slate-800"
            >
              {saving ? "Saving…" : (isEdit ? "Save changes" : "Send invitation")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
